import React from 'react';
import {
  Avatar,
  Checkbox,
  Chip,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { SupplierProduct } from '@domain/suppliers/models/Supplier';
import { Product } from '@domain/product/models/Product';

const formatPrice = (price: number, currency?: string) => {
  if (currency) {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(price);
  }
  return price.toLocaleString();
};

const formatMoq = (link: SupplierProduct): string => {
  if (link.minimumOrderQuantity == null) {
    return '—';
  }
  if (link.minimumOrderQuantityType === 'currency') {
    return formatPrice(link.minimumOrderQuantity, link.currency);
  }
  return `${link.minimumOrderQuantity} items`;
};

const formatMultiplesOf = (link: SupplierProduct): string => {
  if (link.orderInMultiplesOf == null) {
    return '—';
  }
  return String(link.orderInMultiplesOf);
};

export function getSupplierProductDisplayName(row: SupplierProduct): string {
  return row.product.title || row.product.ean || 'Untitled product';
}

export interface SupplierProductRowProps {
  supplierProduct: SupplierProduct;
  selected: boolean;
  onToggleSelect: (productId: string) => void;
  onRowClick?: (product: Product) => void;
}

const SupplierProductRow: React.FC<SupplierProductRowProps> = ({ supplierProduct, selected, onToggleSelect, onRowClick }) => {
  const name = getSupplierProductDisplayName(supplierProduct);


  return (
    <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => onRowClick?.(supplierProduct.product)}>
      <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
        <Checkbox
          checked={selected}
          onChange={() => onToggleSelect(supplierProduct.product.id)}
          inputProps={{ 'aria-label': `Select ${name}` }}
        />
      </TableCell>
      <TableCell sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar
            src={supplierProduct.product.image}
            variant="rounded"
            sx={{ width: 40, height: 40, border: 1, borderColor: 'divider' }}
          >
            {supplierProduct.product.title?.charAt(0).toUpperCase()}
          </Avatar>
          <Stack direction="row" alignItems="center" spacing={1} minWidth={0}>
            <Typography
              variant="body2"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                display: '-webkit-box',
              }}
            >
              {name}
            </Typography>
            {supplierProduct.isMain ? (
              <Chip label="Main" size="small" color="primary" />
            ) : (
              <Typography variant="body2" color="text.secondary" component="span">
                —
              </Typography>
            )}
          </Stack>
        </Stack>
      </TableCell>
      <TableCell align="right" sx={{ py: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          {formatMoq(supplierProduct)}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ py: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          {formatMultiplesOf(supplierProduct)}
        </Typography>
      </TableCell>
      <TableCell align="right" sx={{ py: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          {supplierProduct.purchasePrice != null ? formatPrice(supplierProduct.purchasePrice, supplierProduct.currency) : '—'}
        </Typography>
      </TableCell>
    </TableRow>
  );
};

export default SupplierProductRow;
